/**
 * Pure routing logic split out of `src/app/routes.tsx`: `react-refresh` only
 * allows a component file to also export components, and `resolveStartRoute`
 * plus `ROUTE_PATHS` are plain functions/data, not components.
 */

import type { Me } from '@/api/types';
import { getEdition } from '@/editions';
import type { EditionDefinition } from '@/editions/types';
import type { Permissions } from '@/features/me/permissions';

/**
 * Every path `<Route>` currently answers, absolute and leading-slash. Kept
 * in sync by hand with the tree in `src/app/routes.tsx` —
 * `src/editions/index.test.ts` checks every edition item's `to` against this
 * list ("elke `to` heeft een route").
 */
export const ROUTE_PATHS: readonly string[] = [
  '/',
  '/profile',
  '/profile/consents',
  '/profile/preferences',
  '/departments',
  '/departments/compare',
  '/departments/:departmentId',
  '/departments/:departmentId/edit',
  '/departments/:departmentId/visibility',
  '/departments/:departmentId/collective-consent',
  '/departments/:departmentId/roles',
  '/departments/:departmentId/members/new',
  '/users',
  '/users/:userId',
  '/users/:userId/edit',
  // § Navigatiemodel (PLAN-FASE2.md, WP-02/WP-03): Insights pill, Vitality
  // and Participation subitems.
  '/vitality',
  '/participation',
  '/pilots',
  '/pilots/:pilotId',
  '/challenges',
  // § Navigatiemodel (WP-06): Overview, Home's new first subitem.
  '/overview',
  // § Navigatiemodel (PLAN-FASE3.md WP-02): Content pill, Communicatie
  // subitem.
  '/communications',
  // § Navigatiemodel (PLAN-FASE3.md WP-03): Content pill, Documenten
  // subitem.
  '/documents',
  // § Navigatiemodel (PLAN-FASE3.md WP-04): Content pill, Rapportages
  // subitem.
  '/reports',
  // § Instellingen (PLAN-FASE2.md WP-08), now also the flat sidebar's
  // Instellingen item (§ Hoofdmenu redesign) instead of only the account menu.
  '/settings',
  '/settings/branding',
  '/settings/privacy',
  '/settings/modules',
  '/consents',
  '/subscription',
  '/subscription/checkout',
  '/subscription/return',
  '/subscription/withdraw',
  // § Beheer (WP-13): the company/people/audit/system pages that used to sit
  // at the top level moved under /manage; see src/manage/navigation.ts for
  // the menu that offers these, and LEGACY_MANAGE_REDIRECTS for the three
  // old paths (/companies, /admins, /audit) that still resolve, redirected.
  '/manage',
  '/manage/companies',
  '/manage/companies/new',
  '/manage/companies/:companyId',
  '/manage/companies/:companyId/edit',
  '/manage/companies/:companyId/delete',
  '/manage/companies/:companyId/settings',
  '/manage/companies/:companyId/domains',
  '/manage/companies/:companyId/roles',
  '/manage/companies/:companyId/members/new',
  '/manage/companies/:companyId/departments/new',
  '/manage/users',
  '/manage/users/link',
  '/manage/users/:userId/erase',
  '/manage/admins',
  '/manage/audit',
  '/manage/subscriptions/config',
  '/manage/subscriptions/products',
  '/manage/subscriptions/user-ledger',
  '/manage/subscriptions/webhooks',
  '/manage/subscriptions/price-changes',
  '/manage/system/statistics',
  '/manage/system/requests',
];

/** The first `to` of the first section/item the caller may see in `edition`, or `null` if none is. */
function firstVisibleItemPath(edition: EditionDefinition, permissions: Permissions): string | null {
  for (const section of edition.sections) {
    if (section.visible && !section.visible(permissions)) {
      continue;
    }
    for (const item of section.items ?? []) {
      if (!item.visible || item.visible(permissions)) {
        return item.to;
      }
    }
  }
  return null;
}

/**
 * Where `/` sends the caller (§ Zichtbaarheid per functionaliteit, row
 * "`/` (startroute)"): admin or company admin land on the first thing their
 * edition offers (Departments, in fase 1's Fit/Soccer); a department
 * admin/coach with no wider authority lands on their own department, not the
 * full list ("D ziet zijn afdeling(en) direct, geen 'alle afdelingen'"); a
 * plain department member, a company member without a department, or
 * someone nowhere at all — all fall through to their own profile, same as
 * the edition walk does once Departments/Users are both hidden from them.
 *
 * Not wired to the `/` route yet: `/profile` does not exist as a page in
 * this WP's scope (§ WP-06, "nog geen nieuwe pagina's"), and `App.test.tsx` /
 * `drilldown.test.tsx` still expect `HomePage` there. WP-08 mounts this once
 * the target pages exist.
 */
export function resolveStartRoute(
  me: Me,
  permissions: Permissions,
  edition: EditionDefinition = getEdition(me.user?.edition),
): string {
  const isDepartmentAdminOnly =
    !permissions.isAdmin &&
    permissions.administeredCompanyIds.length === 0 &&
    permissions.administeredDepartmentIds.length > 0;
  if (isDepartmentAdminOnly) {
    return `/departments/${permissions.administeredDepartmentIds[0]}`;
  }
  return firstVisibleItemPath(edition, permissions) ?? '/profile';
}
